import { useState, useEffect } from 'react'
import api from '../api/axios'
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CpuChipIcon,
  ServerStackIcon,
  BellIcon,
  CheckIcon,
  PlusIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  CloudIcon,
  Cog6ToothIcon,
  SignalIcon,
} from '@heroicons/react/24/outline'

const PAGE_SIZE = 12

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'critical'
  uptime: number
  memory: { total: number; free: number; used: number; percentUsed: number }
  cpu: { count: number; loadAverage: number[] }
  database: { status: string; activeConnections?: number; responseTime?: number }
  timestamp: string
}

interface AutoClockoutThresholds {
  regular: { hours: number; source: 'env' | 'default' }
  barge: { hours: number; source: 'env' | 'default' }
}

interface Metric {
  id: string
  metric_type: string
  metric_name: string
  metric_value: number
  metric_unit: string
  status: 'normal' | 'warning' | 'critical'
  recorded_at: string
}

interface Alert {
  id: string
  alert_message: string
  severity: 'info' | 'warning' | 'critical'
  triggered_at: string
  is_resolved: boolean
  metric_value?: number
  threshold_value?: number
}

interface AlertConfig {
  id: string
  alert_name: string
  alert_type: string
  metric_type: string
  threshold_value: number
  comparison: string
  severity: string
  is_enabled: boolean
}

const STATUS_PALETTE = {
  healthy: { text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  running: { text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  degraded: { text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  warning: { text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  critical: { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  error: { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  stopped: { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  unknown: { text: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600' },
}

const getStatusStyle = (s: string) => STATUS_PALETTE[s as keyof typeof STATUS_PALETTE] || STATUS_PALETTE.unknown

function formatUptime(seconds: number) {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  return `${days}d ${hours}h ${mins}m`
}

function formatBytes(bytes: number) {
  const gb = bytes / (1024 * 1024 * 1024)
  if (gb >= 1) return gb.toFixed(1) + ' GB'
  return (bytes / (1024 * 1024)).toFixed(0) + ' MB'
}

function formatTime(iso: string) {
  const d = new Date(iso)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-600 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name ?? p.dataKey}: {p.value}{p.unit || ''}</p>
      ))}
    </div>
  )
}

export default function SystemMonitoring() {
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [autoClockoutThresholds, setAutoClockoutThresholds] = useState<AutoClockoutThresholds | null>(null)
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [alertConfigs, setAlertConfigs] = useState<AlertConfig[]>([])
  const [serviceStatus, setServiceStatus] = useState<any>({})
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('6h')
  const [activeTab, setActiveTab] = useState<'overview' | 'metrics' | 'alerts'>('overview')
  const [currentPage, setCurrentPage] = useState(1)
  const [refreshing, setRefreshing] = useState(false)
  const [alertFilterSeverity, setAlertFilterSeverity] = useState<'all' | 'critical' | 'warning' | 'info'>('all')
  const [sortAlertsBy, setSortAlertsBy] = useState<'recent' | 'severity'>('recent')
  const [metricTypeFilter, setMetricTypeFilter] = useState('all')
  const [metricStatusFilter, setMetricStatusFilter] = useState('all')
  const [showAlertForm, setShowAlertForm] = useState(false)
  const [chartView, setChartView] = useState<'charts' | 'table'>('charts')
  const [newAlert, setNewAlert] = useState({
    alert_name: '', metric_type: 'memory', threshold_value: 80,
    comparison: 'greater_than', severity: 'warning',
  })

  useEffect(() => {
    fetchAll()
    const interval = setInterval(() => { refreshData(); fetchServiceStatus() }, 30000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => { setCurrentPage(1) }, [metricTypeFilter, timeRange])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const metricsUrl = `/super-admin/monitoring/metrics?range=${timeRange}&limit=200`
      const [healthRes, metricsRes, alertsRes, configsRes, svcRes, clockoutRes] = await Promise.all([
        api.get('/super-admin/monitoring/health'),
        api.get(metricsUrl),
        api.get('/super-admin/monitoring/alerts?resolved=false'),
        api.get('/super-admin/monitoring/alert-configs'),
        api.get('/super-admin/monitoring/services'),
        api.get('/super-admin/monitoring/auto-clockout-thresholds'),
      ])
      setSystemHealth(healthRes.data.health)
      setMetrics(metricsRes.data.metrics || [])
      setAlerts(alertsRes.data.alerts || [])
      setAlertConfigs(configsRes.data.configs || [])
      setServiceStatus(svcRes.data || {})
      setAutoClockoutThresholds(clockoutRes.data || null)
    } catch (err) {
      console.error('Error fetching monitoring data:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchServiceStatus = async () => {
    try {
      const res = await api.get('/super-admin/monitoring/services')
      setServiceStatus(res.data || {})
    } catch {}
  }

  const refreshData = async () => {
    try {
      setRefreshing(true)
      const metricsUrl = `/super-admin/monitoring/metrics?range=${timeRange}&limit=200`
      const [healthRes, metricsRes, alertsRes] = await Promise.all([
        api.get('/super-admin/monitoring/health'),
        api.get(metricsUrl),
        api.get('/super-admin/monitoring/alerts?resolved=false'),
      ])
      setSystemHealth(healthRes.data.health)
      setMetrics(metricsRes.data.metrics || [])
      setAlerts(alertsRes.data.alerts || [])
    } catch {} finally { setRefreshing(false) }
  }

  const createAlert = async () => {
    try {
      const res = await api.post('/super-admin/monitoring/alerts', { ...newAlert, is_enabled: true })
      setAlertConfigs(prev => [...prev, res.data.config])
      setNewAlert({ alert_name: '', metric_type: 'memory', threshold_value: 80, comparison: 'greater_than', severity: 'warning' })
      setShowAlertForm(false)
    } catch (err) { console.error('Error creating alert:', err) }
  }

  const resolveAlert = async (id: string) => {
    try {
      await api.post(`/super-admin/monitoring/alerts/${id}/resolve`)
      setAlerts(prev => prev.filter(a => a.id !== id))
    } catch {}
  }

  const clearAllAlerts = async () => {
    if (!window.confirm('Clear all active alerts?')) return
    try {
      await Promise.all(alerts.map(a => api.post(`/super-admin/monitoring/alerts/${a.id}/resolve`)))
      setAlerts([])
    } catch {}
  }

  const toggleAlertConfig = async (id: string, enabled: boolean) => {
    try {
      await api.put(`/super-admin/monitoring/alert-configs/${id}`, { is_enabled: enabled })
      setAlertConfigs(prev => prev.map(c => c.id === id ? { ...c, is_enabled: enabled } : c))
    } catch {}
  }

  const getFilteredMetrics = () => {
    let f = metrics
    if (metricTypeFilter !== 'all') f = f.filter(m => m.metric_type === metricTypeFilter)
    if (metricStatusFilter !== 'all') f = f.filter(m => m.status === metricStatusFilter)
    return f.sort((a, b) => new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime())
  }

  const getMetricStats = () => {
    const f = getFilteredMetrics()
    return {
      total: f.length,
      critical: f.filter(m => m.status === 'critical').length,
      warning: f.filter(m => m.status === 'warning').length,
      normal: f.filter(m => m.status === 'normal').length,
      types: [...new Set(f.map(m => m.metric_type))],
    }
  }

  const getPaginatedMetrics = () => {
    const f = getFilteredMetrics()
    return f.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  }

  const getTotalPages = () => Math.ceil(getFilteredMetrics().length / PAGE_SIZE) || 1

  const getFilteredAlerts = () => {
    let f = alerts
    if (alertFilterSeverity !== 'all') f = f.filter(a => a.severity === alertFilterSeverity)
    if (sortAlertsBy === 'severity') {
      const order = { critical: 0, warning: 1, info: 2 }
      f.sort((a, b) => order[a.severity] - order[b.severity])
    } else {
      f.sort((a, b) => new Date(b.triggered_at).getTime() - new Date(a.triggered_at).getTime())
    }
    return f
  }

  // Build chart data from metrics time series
  const buildChartData = (type: string, limit = 30) => {
    return metrics
      .filter(m => m.metric_type === type)
      .sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime())
      .slice(-limit)
      .map(m => ({ time: formatTime(m.recorded_at), value: Math.round(Number(m.metric_value) * 100) / 100, status: m.status }))
  }

  const memoryChartData = buildChartData('memory')
  const cpuChartData = buildChartData('cpu')
  const dbChartData = buildChartData('database')

  if (loading && !systemHealth) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <div className="h-7 bg-slate-200 rounded w-56 mb-1 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-40 animate-pulse" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse h-28" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">System Monitoring</h1>
              <p className="text-sm text-slate-500 mt-0.5">Real-time infrastructure health &amp; performance</p>
            </div>
            <button onClick={refreshData} disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
              <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing' : 'Refresh'}
            </button>
          </div>
          <div className="flex gap-1 border-b border-slate-200">
            {(['overview', 'metrics', 'alerts'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors capitalize ${
                  activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-900'
                }`}>
                {tab}
                {tab === 'alerts' && alerts.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-white bg-red-600">{alerts.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* ─── OVERVIEW TAB ─── */}
        {activeTab === 'overview' && systemHealth && (
          <div className="space-y-6">
            {/* Service Status */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { key: 'api', label: 'API Server', icon: CloudIcon },
                { key: 'admin', label: 'Admin Portal', icon: Cog6ToothIcon },
                { key: 'db', label: 'Database', icon: ServerStackIcon },
                { key: 'redis', label: 'Redis', icon: SignalIcon },
                { key: 'email', label: 'Email', icon: EnvelopeIcon },
              ].map(svc => {
                const status = serviceStatus[svc.key]?.status || 'unknown'
                const palette = getStatusStyle(status)
                const Icon = svc.icon
                return (
                  <div key={svc.key} className={`bg-white rounded-xl border shadow-sm p-4 flex flex-col items-center gap-2 ${palette.border}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${palette.bg}`}>
                      <Icon className={`w-5 h-5 ${palette.text}`} />
                    </div>
                    <span className="text-xs font-semibold text-slate-700">{svc.label}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${palette.badge}`}>{status}</span>
                    {svc.key === 'redis' && serviceStatus.redis?.status === 'running' && (
                      <span className="text-[10px] text-slate-500 text-center">
                        {serviceStatus.redis.responseTime} ms{serviceStatus.redis.usedMemory ? ` · ${serviceStatus.redis.usedMemory}` : ''}
                      </span>
                    )}
                    {svc.key === 'email' && serviceStatus.email?.provider && (
                      <span className="text-[10px] text-slate-500">{serviceStatus.email.provider}</span>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Background job queues (BullMQ on Redis): waiting / running / failed. */}
            {Array.isArray(serviceStatus.redis?.queues) && serviceStatus.redis.queues.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">Background job queues</h3>
                  <span className="text-xs text-slate-500">
                    Redis {serviceStatus.redis.version || ''}{serviceStatus.redis.connectedClients != null ? ` · ${serviceStatus.redis.connectedClients} connections` : ''}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-2 font-semibold">Queue</th>
                        <th className="px-4 py-2 font-semibold text-right">Waiting</th>
                        <th className="px-4 py-2 font-semibold text-right">Running</th>
                        <th className="px-4 py-2 font-semibold text-right">Failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviceStatus.redis.queues.map((q: { name: string; waiting: number; active: number; failed: number }) => (
                        <tr key={q.name} className="border-t border-slate-100">
                          <td className="px-4 py-2 font-medium text-slate-700">{q.name}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{q.waiting}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{q.active}</td>
                          <td className={`px-4 py-2 text-right tabular-nums ${q.failed > 0 ? 'text-red-600 font-semibold' : ''}`}>{q.failed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Uptime', value: formatUptime(systemHealth.uptime), sub: 'Since last restart', icon: ClockIcon, iconBg: 'bg-blue-50', iconColor: 'text-blue-600' },
                { label: 'CPU Load', value: systemHealth.cpu.loadAverage[0].toFixed(2), sub: `${systemHealth.cpu.count} cores`, icon: CpuChipIcon, iconBg: 'bg-amber-50', iconColor: 'text-amber-600' },
                {
                  label: 'Memory', value: `${systemHealth.memory.percentUsed.toFixed(1)}%`,
                  sub: `${formatBytes(systemHealth.memory.used)} / ${formatBytes(systemHealth.memory.total)}`,
                  icon: ServerStackIcon,
                  iconBg: systemHealth.memory.percentUsed > 80 ? 'bg-red-50' : 'bg-emerald-50',
                  iconColor: systemHealth.memory.percentUsed > 80 ? 'text-red-600' : 'text-emerald-600',
                },
                {
                  label: 'Database', value: systemHealth.database.status,
                  sub: systemHealth.database.responseTime ? `${systemHealth.database.responseTime}ms latency` : `${systemHealth.database.activeConnections ?? 0} connections`,
                  icon: ServerStackIcon,
                  iconBg: systemHealth.database.status === 'healthy' ? 'bg-emerald-50' : 'bg-amber-50',
                  iconColor: systemHealth.database.status === 'healthy' ? 'text-emerald-600' : 'text-amber-600',
                },
                // Auto clock-out thresholds were already being fetched but never
                // rendered, which failed the production build (TS6133) rather than
                // just leaving dead state. `source` matters as much as the number:
                // 'default' means nobody has set it for this deployment.
                ...(autoClockoutThresholds ? [
                  {
                    label: 'Auto clock-out', value: `${autoClockoutThresholds.regular.hours}h`,
                    sub: `Regular shifts \u00b7 ${autoClockoutThresholds.regular.source}`,
                    icon: ClockIcon,
                    iconBg: autoClockoutThresholds.regular.source === 'default' ? 'bg-amber-50' : 'bg-slate-50',
                    iconColor: autoClockoutThresholds.regular.source === 'default' ? 'text-amber-600' : 'text-slate-600',
                  },
                  {
                    label: 'Auto clock-out (barge)', value: `${autoClockoutThresholds.barge.hours}h`,
                    sub: `Barge shifts \u00b7 ${autoClockoutThresholds.barge.source}`,
                    icon: ClockIcon,
                    iconBg: autoClockoutThresholds.barge.source === 'default' ? 'bg-amber-50' : 'bg-slate-50',
                    iconColor: autoClockoutThresholds.barge.source === 'default' ? 'text-amber-600' : 'text-slate-600',
                  },
                ] : []),
              ].map(card => (
                <div key={card.label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">{card.label}</p>
                      <p className="text-xl font-bold text-slate-900 mt-1 capitalize">{card.value}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{card.sub}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.iconBg}`}>
                      <card.icon className={`w-5 h-5 ${card.iconColor}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Memory Usage Bar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Memory Allocation</h3>
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                    <span>Used</span>
                    <span className="font-semibold text-slate-700">{systemHealth.memory.percentUsed.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${systemHealth.memory.percentUsed > 80 ? 'bg-red-500' : systemHealth.memory.percentUsed > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${systemHealth.memory.percentUsed}%` }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: 'Used', val: formatBytes(systemHealth.memory.used) },
                    { label: 'Free', val: formatBytes(systemHealth.memory.free) },
                    { label: 'Total', val: formatBytes(systemHealth.memory.total) },
                  ].map(s => (
                    <div key={s.label} className="bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{s.label}</p>
                      <p className="text-xs font-bold text-slate-800 mt-0.5">{s.val}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Memory Trend Chart */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900">Memory Trend</h3>
                  <span className="text-xs text-slate-400">{memoryChartData.length} data points</span>
                </div>
                {memoryChartData.length > 1 ? (
                  <ResponsiveContainer width="100%" height={140}>
                    <AreaChart data={memoryChartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} domain={[0, 100]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="value" name="Memory %" stroke="#3b82f6" strokeWidth={2} fill="url(#memGrad)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-36 flex items-center justify-center text-slate-300 text-sm">No trend data — increase time range in Metrics tab</div>
                )}
              </div>
            </div>

            {/* CPU Load Chart */}
            {cpuChartData.length > 1 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900">CPU Load Average</h3>
                  <span className="text-xs text-slate-400">{cpuChartData.length} data points</span>
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={cpuChartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="value" name="CPU Load" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* DB response time */}
            {dbChartData.length > 1 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900">Database Response Time</h3>
                  <span className="text-xs text-slate-400">{dbChartData.length} data points</span>
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={dbChartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}ms`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="value" name="Latency (ms)" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* ─── METRICS TAB ─── */}
        {activeTab === 'metrics' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Performance Metrics</h2>
                <p className="text-slate-500 text-sm mt-0.5">Historical data with trend visualization</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setChartView(v => v === 'charts' ? 'table' : 'charts')}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {chartView === 'charts' ? 'Table View' : 'Chart View'}
                </button>
              </div>
            </div>

            {/* Summary cards */}
            {metrics.length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total', val: getMetricStats().total, color: 'text-slate-900' },
                  { label: 'Normal', val: getMetricStats().normal, color: 'text-emerald-600' },
                  { label: 'Warning', val: getMetricStats().warning, color: 'text-amber-600' },
                  { label: 'Critical', val: getMetricStats().critical, color: 'text-red-600' },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs font-medium text-slate-500">{s.label}</p>
                    <p className={`text-3xl font-bold mt-1 ${s.color}`}>{s.val}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Time Range</label>
                  <select value={timeRange} onChange={e => { setTimeRange(e.target.value); refreshData() }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                    <option value="1h">Last Hour</option>
                    <option value="6h">Last 6 Hours</option>
                    <option value="24h">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Metric Type</label>
                  <select value={metricTypeFilter} onChange={e => { setMetricTypeFilter(e.target.value); setCurrentPage(1) }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                    <option value="all">All Types</option>
                    {getMetricStats().types.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Status</label>
                  <select value={metricStatusFilter} onChange={e => { setMetricStatusFilter(e.target.value); setCurrentPage(1) }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                    <option value="all">All</option>
                    <option value="normal">Normal</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Chart view: sparklines per metric type */}
            {chartView === 'charts' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {getMetricStats().types.map(type => {
                  const chartData = buildChartData(type)
                  if (chartData.length < 2) return null
                  const latest = chartData[chartData.length - 1]
                  const colors: Record<string, string> = { memory: '#3b82f6', cpu: '#f59e0b', database: '#8b5cf6', disk: '#10b981', network: '#06b6d4' }
                  const color = colors[type] || '#6366f1'
                  return (
                    <div key={type} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-slate-900 capitalize">{type.replace(/_/g, ' ')}</h3>
                        <div className="text-right">
                          <span className="text-lg font-bold text-slate-900">{latest.value}</span>
                          <span className="text-xs text-slate-400 ml-1">(latest)</span>
                        </div>
                      </div>
                      <ResponsiveContainer width="100%" height={100}>
                        <AreaChart data={chartData} margin={{ top: 2, right: 2, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id={`grad-${type}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={color} stopOpacity={0.15} />
                              <stop offset="95%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={28} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={1.5} fill={`url(#grad-${type})`} dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )
                }).filter(Boolean)}

                {getMetricStats().types.length === 0 && (
                  <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                    <p className="text-sm text-slate-400">No metrics match the current filters</p>
                  </div>
                )}
              </div>
            )}

            {/* Table view */}
            {chartView === 'table' && (
              getFilteredMetrics().length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                  <ExclamationTriangleIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">No metrics match your filters</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-slate-200 bg-slate-50">
                        <tr>
                          {['Metric', 'Type', 'Value', 'Status', 'Recorded'].map(h => (
                            <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {getPaginatedMetrics().map(m => (
                          <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-900">{m.metric_name}</td>
                            <td className="px-4 py-3 text-slate-500">{m.metric_type}</td>
                            <td className="px-4 py-3 font-bold text-slate-900">{m.metric_value} {m.metric_unit}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                m.status === 'normal' ? 'bg-emerald-100 text-emerald-700'
                                  : m.status === 'warning' ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                              }`}>{m.status}</span>
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs">{new Date(m.recorded_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {getTotalPages() > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                      <span className="text-xs text-slate-500">Page {currentPage} of {getTotalPages()} ({getFilteredMetrics().length} total)</span>
                      <div className="flex gap-2">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                          className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">Previous</button>
                        <button onClick={() => setCurrentPage(p => Math.min(getTotalPages(), p + 1))} disabled={currentPage >= getTotalPages()}
                          className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">Next</button>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {/* ─── ALERTS TAB ─── */}
        {activeTab === 'alerts' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Alert Management</h2>
                <p className="text-slate-500 text-sm mt-0.5">Monitor and resolve active system alerts</p>
              </div>
              <div className="flex gap-2">
                {alerts.length > 0 && (
                  <button onClick={clearAllAlerts}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                    Clear All
                  </button>
                )}
                <button onClick={() => setShowAlertForm(!showAlertForm)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 shadow-sm transition-colors">
                  <PlusIcon className="w-4 h-4" /> New Rule
                </button>
              </div>
            </div>

            {showAlertForm && (
              <div className="bg-blue-50 rounded-xl border-2 border-blue-200 p-6">
                <h3 className="text-base font-bold text-slate-900 mb-4">Create Alert Rule</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { label: 'Alert Name', key: 'alert_name', type: 'text', placeholder: 'e.g., High Memory Usage' },
                    { label: 'Threshold Value', key: 'threshold_value', type: 'number', placeholder: '80' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{f.label}</label>
                      <input type={f.type} value={(newAlert as any)[f.key]} placeholder={f.placeholder}
                        onChange={e => setNewAlert(p => ({ ...p, [f.key]: f.type === 'number' ? parseInt(e.target.value) : e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                    </div>
                  ))}
                  {[
                    { label: 'Metric Type', key: 'metric_type', opts: [['memory', 'Memory Usage'], ['cpu', 'CPU Load'], ['disk', 'Disk Space'], ['database', 'Database']] },
                    { label: 'Comparison', key: 'comparison', opts: [['greater_than', 'Greater Than'], ['less_than', 'Less Than'], ['equal', 'Equal To']] },
                    { label: 'Severity', key: 'severity', opts: [['info', 'Info'], ['warning', 'Warning'], ['critical', 'Critical']] },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{f.label}</label>
                      <select value={(newAlert as any)[f.key]} onChange={e => setNewAlert(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                        {f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={createAlert}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">Create</button>
                  <button onClick={() => setShowAlertForm(false)}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <BellIcon className="w-4 h-4" /> Active Alerts
                    {alerts.length > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-white bg-red-600">{alerts.length}</span>
                    )}
                  </h3>
                  {alerts.length > 0 && (
                    <div className="flex gap-2">
                      <select value={alertFilterSeverity} onChange={e => setAlertFilterSeverity(e.target.value as any)}
                        className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                        <option value="all">All</option>
                        <option value="critical">Critical</option>
                        <option value="warning">Warning</option>
                        <option value="info">Info</option>
                      </select>
                      <select value={sortAlertsBy} onChange={e => setSortAlertsBy(e.target.value as any)}
                        className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                        <option value="recent">Newest</option>
                        <option value="severity">Severity</option>
                      </select>
                    </div>
                  )}
                </div>

                {alerts.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700">
                    <CheckCircleIcon className="w-10 h-10" />
                    <p className="text-sm font-semibold">All clear — no active alerts</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {getFilteredAlerts().map(alert => (
                      <div key={alert.id} className={`p-4 rounded-xl border-l-4 ${
                        alert.severity === 'critical' ? 'bg-red-50 border-l-red-500'
                          : alert.severity === 'warning' ? 'bg-amber-50 border-l-amber-500'
                            : 'bg-blue-50 border-l-blue-500'
                      }`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 text-sm">{alert.alert_message}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{new Date(alert.triggered_at).toLocaleString()}</p>
                            {alert.metric_value !== undefined && (
                              <p className="text-xs text-slate-400 mt-0.5">Value: {alert.metric_value} · Threshold: {alert.threshold_value}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold text-white ${
                              alert.severity === 'critical' ? 'bg-red-600' : alert.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-600'
                            }`}>{alert.severity}</span>
                            <button onClick={() => resolveAlert(alert.id)} title="Resolve"
                              className="p-1.5 rounded-lg hover:bg-white/60 transition-colors">
                              <CheckIcon className="w-4 h-4 text-emerald-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4">
                  <Cog6ToothIcon className="w-4 h-4" /> Alert Rules
                  <span className="ml-auto text-xs text-slate-400 font-normal">{alertConfigs.length} rules</span>
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {alertConfigs.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No rules configured</p>
                  ) : alertConfigs.map(cfg => (
                    <div key={cfg.id} className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-800">{cfg.alert_name}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{cfg.metric_type} {cfg.comparison.replace('_', ' ')} {cfg.threshold_value}</p>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold mt-1 ${
                          cfg.severity === 'critical' ? 'bg-red-100 text-red-700' : cfg.severity === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>{cfg.severity}</span>
                      </div>
                      <button
                        role="switch"
                        aria-checked={cfg.is_enabled}
                        onClick={() => toggleAlertConfig(cfg.id, !cfg.is_enabled)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 mt-0.5 ${cfg.is_enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${cfg.is_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
